import React, { useState } from "react";
import { Album, EnhancedFolder, EventType, Folder } from "./types";
import SidebarTreeItem from "./SidebarTreeItem";
import Telemetry from "../../../utils/rudderstackAnalytics";
import {
  findEntityPath,
  getEncodedEntityPath,
  getEntitySegment,
  EntityType,
  typeMap,
  isEventSelected,
  isAlbumSelected,
  getSelectionClass
} from "./FolderFeatures/EncryptionOfFolder";

const eventFile = require("../styles/images/Event.svg");
const albumIcon = require("../styles/images/gallery.svg");
const createAlbum = require("../styles/images/createAlbum.svg");
const DownArrow = require("../styles/images/DownArrow.svg");
const SideArrow = require("../styles/images/SideArrow.svg");
const shortcutIcon = require("../styles/images/shortcut-icon.svg");

interface EventListProps {
  events: EventType[];
  handleEventClick: (
    event: EventType,
    encodedPath: string,
    open?: boolean
  ) => void;
  albums: Record<number, Album[]>;
  openedEvents: Set<number>;
  generateEventInstanceId: (event: EventType, parentPath?: string) => string;
  isEventInstanceExpanded: (eventInstanceId: string) => boolean;
  handleAlbumClick: (
    album: Album,
    encodedPath: string,
    event: EventType
  ) => void;
  handleEventCollapse: (event: any, parentPath?: string) => void;
  selectedAlbum: Album | null;
  selectedEvent: EventType | null;
  jumpToContentView: () => void;
  isMobile: boolean;
  currentPage: number;
  handlePageChange: (page: number) => void;
  nextPage: null | number;
  loadMore: () => void;
  albumLoadingEventId: number | null;
  eventsLoading: boolean;
  setShowNewAlbumModal: (eventId: number) => void;
  onFolderCollapse: (folder: Folder, parentPath?: string) => void;
  getFolderPath?: (
    folder: EnhancedFolder,
    rootFolders: EnhancedFolder[]
  ) => string;
  openedFolders: Set<string>;
  folderLoadingId: number | null;
  handleURLChange: () => void;
  handleFolderClickInSidebar: (
    folder: EnhancedFolder,
    encodedPath: string
  ) => void;
  rootFolders: Folder[];
  folderId?: number | null;
  libraryRootFolders?: EnhancedFolder[];
  setEncodedString: (encodedString: string) => void;
  currentEncodedPath?: string | null;
  parentPath?: string;
}

const EventList: React.FC<EventListProps> = ({
  events,
  handleEventClick,
  albums,
  openedEvents,
  generateEventInstanceId,
  isEventInstanceExpanded,
  handleAlbumClick,
  handleEventCollapse,
  selectedAlbum,
  selectedEvent,
  jumpToContentView,
  isMobile,
  nextPage,
  loadMore,
  albumLoadingEventId,
  eventsLoading,
  setShowNewAlbumModal,
  handleFolderClickInSidebar,
  onFolderCollapse,
  getFolderPath,
  openedFolders,
  folderLoadingId,
  handleURLChange,
  rootFolders,
  folderId,
  libraryRootFolders,
  setEncodedString,
  currentEncodedPath,
  parentPath = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const shouldShowList =
    !isMobile ||
    (isMobile && (!folderId ? isOpen : true));

  const showTooltip = (name: string, type: "event" | "album") =>
    (type === "event" && name.length > 28) ||
    (type === "album" && name.length > 18);

  const handleTooltip = (id: string, name: string, type: "show" | "hide") => {
    if (type === "show") {
      //@ts-ignore
      $(`#${id}`).attr("data-original-title", name).tooltip("show");
    } else {
      //@ts-ignore
      $(`#${id}`).tooltip();
    }
  };

  const handleEventClickWithNavigation = (event: EventType) => {
    let path = "";

    // If we have a parent path context (from sidebar navigation), use it
    // This ensures shortcut sub-events maintain the correct breadcrumb hierarchy
    if (parentPath) {
      const eventSegment = getEntitySegment(event);
      path = `${parentPath}/${eventSegment}`;
    } else if (event.hierarchy && event.hierarchy.length > 0) {
      const hierarchySegments = event.hierarchy.map(h => {
        const prefix = typeMap[h.entityType as EntityType];
        return `${prefix}:${h.id}`;
      });

      path = [...hierarchySegments, getEntitySegment(event)].join("/");
    } else {
      const segment = getEntitySegment(event);
      path = segment;
    }

    const encodedPath = getEncodedEntityPath(path);

    if (encodedPath) {
      setEncodedString(encodedPath);
      handleEventClick(event, encodedPath);
    } else {
      console.error("Failed to encode path", path);
    }

    // Also expand the event in sidebar to match arrow state
    handleEventCollapse(event, parentPath);
  };

  const handleAlbumClickWithNavigation = (album: Album, event: EventType) => {
    let eventPathTrail;

    // If we have a parent path context, use it to construct the event path
    if (parentPath) {
      const eventSegment = getEntitySegment(event);
      eventPathTrail = `${parentPath}/${eventSegment}`;
    } else {
      // First try to find the event in the folder hierarchy
      if (libraryRootFolders && libraryRootFolders.length > 0) {
        eventPathTrail = findEntityPath(
          libraryRootFolders,
          event.referenceId || event.id,
          "event"
        );
      }

      // If not found in folders, construct path for root event
      if (!eventPathTrail) {
        eventPathTrail = getEntitySegment(event);
      }
    }

    const albumSegment = getEntitySegment(album);
    const fullPath = `${eventPathTrail}/${albumSegment}`;
    const encodedPath = getEncodedEntityPath(fullPath);

    if (encodedPath) {
      setEncodedString(encodedPath);
      handleAlbumClick(album, encodedPath, event);
    }
  };

  const isLoadingAlbumsForEvent = (eventId: number) =>
    albumLoadingEventId === eventId;

  const renderAlbumList = (event: EventType) => {
    const eventInstanceId = generateEventInstanceId(event, parentPath);
    if (!isEventInstanceExpanded(eventInstanceId)) return null;
    if (isLoadingAlbumsForEvent(event.referenceId || event.id)) {
      return (
        <div className="event-list-inline-spinner">
          <span className="fa fa-spinner fa-spin"></span>
        </div>
      );
    }

    const eventAlbums = albums[event.id] || [];

    return (
      <ul>
        {eventAlbums.map(album => (
          <li
            key={album.id}
            className="ws-album-border"
            onClick={() => {
              handleAlbumClickWithNavigation(album, event);
              if (isMobile) jumpToContentView();
              Telemetry.track("content-library:album-opened", {
                source: "directory-view"
              });
            }}
          >
            <div
              className={`ws-album-name-icon ${getSelectionClass(
                isAlbumSelected(album.id, currentEncodedPath || null)
              )}`}
            >
              <img src={albumIcon} className="ws-album-combo-name-icon" />
              <span
                className="ws-album-name"
                id={`ws-album-name-tooltip-${album.id}`}
                data-toggle="tooltip"
                aria-hidden="true"
                onMouseEnter={() =>
                  showTooltip(album.name, "album") &&
                  handleTooltip(
                    `ws-album-name-tooltip-${album.id}`,
                    album.name,
                    "show"
                  )
                }
                onMouseMove={() =>
                  showTooltip(album.name, "album") &&
                  handleTooltip(
                    `ws-album-name-tooltip-${album.id}`,
                    album.name,
                    "hide"
                  )
                }
              >
                {album.name}
              </span>
            </div>
          </li>
        ))}
        {event.canCreateAlbums && (
          <li className="ws-album-border">
            <div
              onClick={() => {
                return setShowNewAlbumModal(event.id);
              }}
              className="ws-create-album-btn"
            >
              <img src={createAlbum} className="ws-create-album-icon" />
              <span>Create an album</span>
            </div>
          </li>
        )}
      </ul>
    );
  };

  const renderEventItem = (event: EventType) => {
    const eventInstanceId = generateEventInstanceId(event, parentPath);
    const isExpanded = isEventInstanceExpanded(eventInstanceId);
    const isEventInCurrentPath = isEventSelected(
      event.isReference && event.referenceId ? event.referenceId : event.id,
      currentEncodedPath || null,
      event.isReference,
      event.referenceId
    );

    return (
      <li
        key={event.referenceId || event.id}
        className={`ws-event-item ${
          folderId !== null ? "nested" : ""
        } ${isExpanded ? "open" : ""} ${getSelectionClass(
          isEventInCurrentPath
        )}`}
      >
        <div className="ws-group-name-arrow">
          <div
            className="ws-arrow-spacing"
            onClick={() => handleEventCollapse(event, parentPath)}
            style={{
              marginLeft: isExpanded ? 0 : 3,
              marginRight: isExpanded ? 0 : 2
            }}
          >
            <img
              src={isExpanded ? DownArrow : SideArrow}
              alt="collapse-arrow"
            />
          </div>

          <span
            className="ws-event-group"
            onClick={() => {
              handleEventClickWithNavigation(event);
            }}
            id={`ws-event-name-tooltip-${event.id}`}
            aria-hidden="true"
          >
            <img src={eventFile} className="ws-file-spacing" />
            {event.isReference && (
              <img src={shortcutIcon} className="ws-shortcut-icon-sidebar " />
            )}
            <span
              className={`ws-event-name ${
                isLoadingAlbumsForEvent(event.id)
                  ? "half-width-text"
                  : "full-width-text"
              }`}
            >
              {event.name}
            </span>
          </span>
        </div>
        {renderAlbumList(event)}
      </li>
    );
  };

  const renderMobileToggle = () =>
    isMobile &&
    !folderId && (
      <div className="ws-toggle-container">
        <button
          className="ws-toggle-sidebar-btn"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>Jump to an event or album</span>
          <img src={isOpen ? DownArrow : SideArrow} alt="toggle" />
        </button>
      </div>
    );

  return (
    <>
      {renderMobileToggle()}
      <div
        className={
          isMobile ? `ws-content ${shouldShowList ? "active" : ""}` : ""
        }
        style={{
          display: shouldShowList ? "block" : "none",
          marginTop: 0,
          paddingLeft: isMobile ? 15 : 0,
          maxHeight: isMobile ? 350 : "unset"
        }}
      >
        {isMobile && !folderId && <hr />}
        {isMobile &&
          !folderId &&
          rootFolders.map(folder => (
            <SidebarTreeItem
              key={folder.id}
              folder={folder}
              onFolderClick={handleFolderClickInSidebar}
              onEventClick={handleEventClickWithNavigation}
              generateEventInstanceId={generateEventInstanceId}
              isEventInstanceExpanded={isEventInstanceExpanded}
              albums={albums}
              onAlbumClick={handleAlbumClick}
              openedEvents={openedEvents}
              openedFolders={openedFolders}
              onEventCollapse={handleEventCollapse}
              onFolderCollapse={onFolderCollapse}
              getFolderPath={getFolderPath}
              selectedAlbum={selectedAlbum}
              selectedEvent={selectedEvent}
              albumLoadingEventId={albumLoadingEventId}
              setShowNewAlbumModal={setShowNewAlbumModal}
              isMobile={true}
              jumpToContentView={jumpToContentView}
              folderLoadingId={folderLoadingId}
              handleURLChange={handleURLChange}
              libraryRootFolders={libraryRootFolders}
              setEncodedString={setEncodedString}
              parentPath={parentPath}
            />
          ))}
        {events.length > 0 && events.map(renderEventItem)}
        {nextPage && events.length > 0 && (
          <div className="ws-load-more-container">
            <button
              className="geo-btn geo-btn--small geo-btn--secondary geo-btn--caps"
              onClick={loadMore}
            >
              Load More{" "}
              {eventsLoading && (
                <span className="fa fa-spinner fa-spin ws-color-white"></span>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default EventList;
